import React from "react";
import { Shield } from "lucide-react";
import {
  formatGameDate,
  formatGameTime,
} from "@/v2/pages/Events/date-utils";

/**
 * The strip under the player for a game with no stats to show.
 *
 * Two tenants have `config.v2.gameDetails` on; the other eleven had this space
 * render nothing at all, because CondensedGameClips returns null on an empty
 * clip list and the GameSheet panel is gated off. A game with no condensed
 * reel and no box score left the page ending abruptly under the clip bar.
 *
 * Everything here comes from the tenant's own player API
 * (`/player/{gameId}?customer={n}`), which the watch page has already fetched
 * before this renders. No second request, and nothing tenant-specific: every
 * tenant returns these fields, which is what makes this the safe fallback.
 *
 * It deliberately mirrors GameDetailsFallback's shell (same border, same
 * padding, same crest size, same separator) minus the centre column, so a
 * tenant that later switches the stats on sees the same card grow a middle
 * rather than a different card appear.
 */

export type TeamNameInfo = { city: string; mascot: string };

function TeamCrest({ logo }: { logo: string | null }) {
  if (logo) {
    return (
      <img src={logo} alt="" className="w-16 h-16 shrink-0 object-contain" />
    );
  }
  return (
    <Shield className="size-12 shrink-0 text-slate-300" aria-hidden="true" />
  );
}

function TeamSide({
  name,
  logo,
  align,
}: {
  name: TeamNameInfo;
  logo: string | null;
  align: "left" | "right";
}) {
  const hasSplitName = Boolean(name.city && name.mascot);

  return (
    <div
      className={
        "v2-game-details-team flex flex-1 items-center " +
        (align === "left" ? "justify-start" : "justify-end")
      }
      style={{ gap: "12px" }}
    >
      <TeamCrest logo={logo} />
      <div
        className="v2-game-details-team-info flex flex-col items-start text-left"
        style={{ gap: "1px" }}
      >
        {hasSplitName ? (
          <>
            <div
              className="text-sm font-bold uppercase"
              style={{ color: "var(--v2-primary)" }}
            >
              {name.city}
            </div>
            <div
              className="text-lg font-extrabold uppercase text-slate-900"
              style={{ lineHeight: 1.1 }}
            >
              {name.mascot}
            </div>
          </>
        ) : (
          <div
            className="text-lg font-extrabold uppercase text-slate-900"
            style={{ lineHeight: 1.1 }}
          >
            {name.city || name.mascot}
          </div>
        )}
      </div>
    </div>
  );
}

export function GameMatchupCard({
  awayTeamName,
  homeTeamName,
  awayTeamLogo,
  homeTeamLogo,
  isLive = false,
  gameDateTime,
  facilityName,
  cardDescription,
}: {
  awayTeamName: TeamNameInfo;
  homeTeamName: TeamNameInfo;
  awayTeamLogo?: string | null;
  homeTeamLogo?: string | null;
  isLive?: boolean;
  /** Raw `game_date_time` from the player API. Converted here, not by the caller. */
  gameDateTime?: string | null;
  facilityName?: string | null;
  cardDescription?: string | null;
}) {
  /*
   * Nothing to say means nothing to render. A card carrying two blank crests
   * and the word "at" is worse than the empty space this replaces.
   */
  const hasNames = Boolean(
    awayTeamName?.city || awayTeamName?.mascot ||
    homeTeamName?.city || homeTeamName?.mascot,
  );
  if (!hasNames) return null;

  /*
   * The same two functions the schedule uses, not a second way of saying the
   * same thing. `game_date_time` arrives with no offset ("2026-09-01 06:00 PM")
   * and is Eastern by convention; parseGameDateTime encodes that rule and these
   * render it in the viewer's own zone with the abbreviation. Printing the raw
   * string here had the card and the schedule disagreeing about when the same
   * game starts.
   */
  const when = [
    formatGameDate(gameDateTime ?? undefined),
    formatGameTime(gameDateTime ?? undefined),
  ]
    .filter(Boolean)
    .join(" · ");

  const metaLine = [cardDescription, facilityName, when]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className="v2-game-details-card flex flex-col border border-slate-200 bg-white"
      style={{ padding: "12px 20px", gap: "8px" }}
    >
      <div
        className="v2-game-details-row flex items-center"
        style={{ gap: "24px" }}
      >
        {/* Away team first (visitor "at" home), per standard sports order. */}
        <TeamSide name={awayTeamName} logo={awayTeamLogo ?? null} align="left" />

        <div className="text-center text-[13px] text-slate-400 font-medium">
          {isLive ? (
            <span className="font-extrabold text-base text-live">VS</span>
          ) : (
            "at"
          )}
        </div>

        <TeamSide name={homeTeamName} logo={homeTeamLogo ?? null} align="right" />
      </div>

      {metaLine && (
        <div
          className="flex flex-col items-center text-center"
          style={{ borderTop: "1px solid #e2e8f0", paddingTop: "8px" }}
        >
          <div className="text-xs text-slate-500">{metaLine}</div>
        </div>
      )}
    </div>
  );
}

export default GameMatchupCard;
